import React, { useEffect, useState } from 'react';
import { Box, IconButton, Paper, Typography, Button, Dialog, DialogContent, DialogActions, Stack, TextField, Grid, Divider, Alert } from '@mui/material';
import { AddCircleOutline, Save, Close, DeleteOutline, Visibility } from '@mui/icons-material';
import InputForm from "../../../InputT";
import SearInputG from "../../../SearchInput";
import SearchModal from '../../../SearchModal';
import ParamsPicklist from '../../../../Types/ParamsPicklist';
import SweetAlert from "sweetalert2";
import { Select, Upload, Form, Input, Modal, Tag } from 'antd';
import AntTable from '../../../TableResizable';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import CheckBox from "../../../CheckBox";
import { useAuthHeader } from '../../../../Services/Auth/sessionAuth';
import { ActifDocumentType, extractActifDocumentRows, getActifDocumentLabel, loadActifDocumentTypes, toDocumentDataUri } from '../../../../Services/actifDocumentTypes';
import axios from 'axios';
import Data from '../../../../Data/Data.json';
import { Crypter } from '../../../../Functions/Security/Crypter';

const { Option } = Select;

const searchCat: ParamsPicklist = { CodeAgence: '', Table: "CategorieActif", Colonne1: "CodeCategorie", Colonne2: "Libelle", Colagence: '101', Choix: 22 };
const searchFam: ParamsPicklist = { CodeAgence: '', Table: "FamilleActif", Colonne1: "CodeFamille", Colonne2: "Libelle", Colagence: '101', Choix: 22 };
const searchFournisseur: ParamsPicklist = { CodeAgence: '', Table: "Fournisseur", Colonne1: "CodeFournisseur", Colonne2: "RaisonSociale", Colagence: '101', Choix: 22 };

interface ParentColumn {
    key: string;
    label: string;
    width?: number;
    align?: 'left' | 'center' | 'right';
    render: (row: any, index: number, actions: { onEdit: (key: string) => void; }) => React.ReactNode;
}

interface Tab1Props {
    form?: any;
    glActifs: any[];
    setglActifs: React.Dispatch<React.SetStateAction<any[]>>;
    categoryConsulted: boolean;
    listFinancements?: any[];
    columns: ParentColumn[];
    disabledForm: boolean;
    onSaveDirect: (actif: any) => void;
    codeCategorieConsultee: string;
    consultTrigger?: number;
}

const emptyAssetForm = {
    CODEACTIF: '',
    IDENTIFIANTUNIQUE: '',
    DESIGNATION: '',
    COUTACHAT: 0,
    VALEURRESIDUELLE: 0,
    FOURNISSEUR: '',
    lib_CODEFOURNISSEUR: '',
    LOCALISATION: '',
    COMMENTAIREVALIDATION: '',
    STATUTVALIDATION: 'EN_ATTENTE',
    STATUT: 'EN_ACQUISITION',
    GRADE: 'A',
    DOCUMENTS: []
};

// FIX : convertit un actif tel que renvoyé par le backend (CODEGRADE,
// COUTINITIAL, LOCALISATIONCOURANTE, CODEFOURNISSEUR/LIBELLEFOURNISSEUR)
// vers les noms de champs locaux utilisés par la modale (GRADE, COUTACHAT,
// LOCALISATION, FOURNISSEUR/lib_CODEFOURNISSEUR). Sans cette conversion,
// l'édition d'un actif existant affichait ces champs vides.
const mapAssetToForm = (asset: any) => ({
    ...emptyAssetForm,
    ...asset,
    GRADE: asset.CODEGRADE || asset.GRADE || 'A',
    COUTACHAT: asset.COUTINITIAL ?? asset.COUTACHAT ?? 0,
    LOCALISATION: asset.LOCALISATIONCOURANTE ?? asset.LOCALISATION ?? '',
    FOURNISSEUR: asset.CODEFOURNISSEUR ?? asset.FOURNISSEUR ?? '',
    lib_CODEFOURNISSEUR: asset.LIBELLEFOURNISSEUR ?? asset.lib_CODEFOURNISSEUR ?? '',
    DOCUMENTS: (asset.DOCUMENTS ?? asset.documents ?? []).map((document: any) => ({
        _id: document._id || `${document.TypeCodeDoc ?? document.TYPECODEDOC}-${Math.random().toString(36).slice(2)}`,
        NumDocument: document.NumDocument ?? document.NUMDOCUMENT ?? document.NumeroDocument ?? document.NUMERODOCUMENT ?? null,
        TypeCodeDoc: document.TypeCodeDoc ?? document.TYPECODEDOC,
        Valeur: toDocumentDataUri(document) ?? document.Valeur ?? document.VALEUR,
        FileName: document.FileName ?? document.FILENAME ?? document.NomFichier,
        LibelleDoc: document.LibelleDoc ?? document.LIBELLEDOC
    }))
});

const Tab1 = (props: Tab1Props) => {
    const authHeader = useAuthHeader();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [assetForm, setAssetForm] = useState<any>({ ...emptyAssetForm });
    const [search, setSearch] = useState('');
    const [uploadingField, setUploadingField] = useState<string | null>(null);
    const [selectedDocumentType, setSelectedDocumentType] = useState<string | undefined>();
    const [previewedDocument, setPreviewedDocument] = useState<any>(null);
    const [deletedDocumentNums, setDeletedDocumentNums] = useState<number[]>([]);
    const [documentDefinitions, setDocumentDefinitions] = useState<ActifDocumentType[]>([]);

    useEffect(() => {
        loadActifDocumentTypes(authHeader()).then(setDocumentDefinitions).catch(() => setDocumentDefinitions([]));
    }, [authHeader]);

    const [filtreStatut, setFiltreStatut] = useState<'TOUS' | 'EN_ATTENTE' | 'REJETE'>('TOUS');

    useEffect(() => {
        setFiltreStatut('TOUS');
        setSearch('');
    }, [props.consultTrigger]);

    const countPending = props.glActifs.filter((a: any) => (a.STATUTVALIDATION || 'EN_ATTENTE') === 'EN_ATTENTE').length;
    const countRejected = props.glActifs.filter((a: any) => a.STATUTVALIDATION === 'REJETE').length;
    const dataAffichees = props.glActifs.filter((a: any) => {
        if (a.STATUT === 'ANNULE') return false;
        const currentStatut = a.STATUTVALIDATION || 'EN_ATTENTE';
        const matchesStatus = filtreStatut === 'TOUS' ? true : currentStatut === filtreStatut;
        const matchesSearch = Object.values(a).some(value => String(value ?? '').toLowerCase().includes(search.toLowerCase()));
        return matchesStatus && matchesSearch;
    });

    const handleUpload = (file: File, typeCodeDoc: string) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            setAssetForm((prev: any) => ({
                ...prev,
                DOCUMENTS: [
                    ...(prev.DOCUMENTS || []),
                    { _id: `${typeCodeDoc}-${Date.now()}-${Math.random().toString(36).slice(2)}`, TypeCodeDoc: typeCodeDoc, Valeur: reader.result, FileName: file.name }
                ]
            }));
            setUploadingField(null);
            setSelectedDocumentType(undefined);
            SweetAlert.fire({ title: "Document chargé", text: `${file.name} est prêt`, icon: "success", timer: 1000, showConfirmButton: false });
        };
        reader.onerror = () => setUploadingField(null);
        setUploadingField(typeCodeDoc);
        return false;
    };

    const removeDocument = (documentId: string) => {
        const removed = (assetForm.DOCUMENTS || []).find((document: any) => document._id === documentId);
        if (removed?.NumDocument != null) {
            setDeletedDocumentNums(prev => [...prev, removed.NumDocument]);
        }
        setAssetForm((prev: any) => ({
            ...prev,
            DOCUMENTS: (prev.DOCUMENTS || []).filter((document: any) => document._id !== documentId)
        }));
    };

    const resetSupplierFields = () => {
        props.form?.resetFields(['FOURNISSEUR', 'lib_CODEFOURNISSEUR']);
    };

    const openAddAsset = () => {
        setEditingIndex(null);
        setAssetForm({ ...emptyAssetForm });
        setSelectedDocumentType(undefined);
        setPreviewedDocument(null);
        setDeletedDocumentNums([]);
        resetSupplierFields();
        setIsModalOpen(true);
    };

    // FIX : mapping vers les noms de champs backend + tous les champs obligatoires
    // (sauf documents), + correction Grade/Fournisseur/Localisation.
    const openEditAsset = async (index: number) => {
        setEditingIndex(index);
        setPreviewedDocument(null);
        setSelectedDocumentType(undefined);
        setDeletedDocumentNums([]);
        const asset = props.glActifs[index];
        const mapped = mapAssetToForm(asset);
        setAssetForm(mapped);
        props.form?.setFieldsValue({ FOURNISSEUR: mapped.FOURNISSEUR, lib_CODEFOURNISSEUR: mapped.lib_CODEFOURNISSEUR });
        setIsModalOpen(true);

        // Charger les documents déjà enregistrés en BD pour cet actif
        if (asset.CODEACTIF) {
            try {
                const getActifWithDocuments = (Data.ApiUrl as any).GetActifWithDocuments;
                const useNewEndpoint = !!getActifWithDocuments;
                const headers: any = {
                    'Authorization': authHeader(),
                    'CypherCodeActif': Crypter.encrypt(asset.CODEACTIF)
                };
                if (!useNewEndpoint) {
                    headers['CypherStatutValidation'] = Crypter.encrypt(asset.STATUTVALIDATION || 'EN_ATTENTE');
                }
                const response = await axios.get(useNewEndpoint ? getActifWithDocuments : Data.ApiUrl.GetActifsByValidation, { headers });
                const res = JSON.parse(Crypter.decrypt(response.data)).Value;
                if (res.StatutCode === 200) {
                    const docRows = extractActifDocumentRows(res.Result, asset.CODEACTIF);
                    const persistedDocs = docRows.map((document: any) => ({
                        _id: `persisted-${document.NumDocument ?? document.NUMDOCUMENT ?? document.NumeroDocument ?? document.NUMERODOCUMENT ?? Math.random().toString(36).slice(2)}`,
                        NumDocument: document.NumDocument ?? document.NUMDOCUMENT ?? document.NumeroDocument ?? document.NUMERODOCUMENT ?? null,
                        TypeCodeDoc: document.TypeCodeDoc ?? document.TYPECODEDOC,
                        Valeur: toDocumentDataUri(document),
                        FileName: document.FileName ?? document.FILENAME ?? document.NomFichier ?? document.NOMFICHIER,
                        LibelleDoc: getActifDocumentLabel(document, documentDefinitions)
                    }));
                    // Fusionner : les docs persistés remplacent uniquement les
                    // anciens docs persistés, les uploads locaux sont conservés.
                    setAssetForm((prev: any) => ({
                        ...prev,
                        DOCUMENTS: [
                            ...persistedDocs,
                            ...(prev.DOCUMENTS || []).filter((d: any) => !String(d._id || '').startsWith('persisted-') && d.NumDocument == null)
                        ]
                    }));
                }
            } catch (error) {
                console.error('Erreur chargement documents existants:', error);
            }
        }
    };

    const closeAssetModal = () => {
        setIsModalOpen(false);
        setEditingIndex(null);
        setAssetForm({ ...emptyAssetForm });
        setSelectedDocumentType(undefined);
        setPreviewedDocument(null);
        setDeletedDocumentNums([]);
        resetSupplierFields();
    };

    const handleAddOrUpdateAsset = async () => {
        const fCode = props.form?.getFieldValue('FOURNISSEUR');
        const fLib = props.form?.getFieldValue('lib_CODEFOURNISSEUR');

        // --- VALIDATION : tous les champs obligatoires, sauf les documents ---
        if (!assetForm.DESIGNATION?.trim()) {
            SweetAlert.fire("Champ requis", "La désignation est obligatoire.", "warning");
            return;
        }
        if (!fCode) {
            SweetAlert.fire("Champ requis", "Veuillez sélectionner un fournisseur.", "warning");
            return;
        }
        if (!assetForm.COUTACHAT || assetForm.COUTACHAT <= 0) {
            SweetAlert.fire("Champ requis", "Le coût d'achat doit être supérieur à 0.", "warning");
            return;
        }

        const finalAsset = {
            ...assetForm,
            FOURNISSEUR: fCode,
            lib_CODEFOURNISSEUR: fLib,
            DocumentsToDelete: deletedDocumentNums
        };

        if (props.onSaveDirect) {
            props.onSaveDirect(finalAsset);
            closeAssetModal();
        }
    };

    const rowActions = {
        onEdit: async (key: string) => { 
            const i = props.glActifs.findIndex((a: any) => a._key === key); 
            if (i !== -1) await openEditAsset(i); 
        }
    };

    const antdColumns: ColumnsType<any> = props.columns.map((col: ParentColumn) => ({
        title: col.label, dataIndex: col.key, key: col.key, width: col.width, align: col.align || 'left',
        render: (_v: any, record: any, index: number) => col.render(record, index, rowActions)
    }));

    return (
        <div className='tabItemAuto'>
            <Box className='border' sx={{ width: "85%", position: "absolute" }}>
                <legend> CONTEXTE DE LA CATÉGORIE (consultation informative) </legend>

                <Box sx={{ p: 2, mt: -2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6} lg={6} sx={{ pr: 2 }}>
                            <SearchModal width={5} disabled={false} notRequired form={props.form} Titre={'Recherche Catégorie'} label={'Code Catégorie'} name={'CODECATEGORIE'} Parametre={searchCat} />
                            <InputForm width={5} label="Libellé" type="texte" name="LIBELLE" disabled={true} />
                            <SearInputG width={5} form={props.form} Titre={'Recherche Famille'} label={'Famille'} name={'FAMILLE'} Parametre={searchFam} dissabledCode={true} />
                        </Grid>
                        <Grid item xs={12} md={6} lg={6} className='border-left' sx={{ pl: 2 }}>
                            <InputForm width={6} label="Règles d'usage" type="TextArea" name="REGLESUSAGE" disabled={true} row={1} />

                            <Form.Item label="Financements" name="FINANCEMENT_ELIGIBLES" labelCol={{ span: 6 }} labelAlign="left">
                                <Select mode="multiple" placeholder="Types autorisés" style={{ width: '100%' }} disabled={props.disabledForm}>
                                    {props.listFinancements?.map((f: any) => <Option key={f.CODE} value={f.CODE}>{f.LIBELLE}</Option>)}
                                </Select>
                            </Form.Item>

                            <Box sx={{ ml: `${(7 / 24) * 100}%`, mt: 2, display: 'flex', alignItems: 'center', gap: 2, '& .ant-form-item': { marginBottom: 0 } }}>
                                <CheckBox notRequired form={props.form} label="Actif" name="ESTACTIF" disabled={props.disabledForm} />
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={{ p: 2 }}>
                    <Paper elevation={2} sx={{ p: 2, borderRadius: 3 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                            <Box>
                                <Typography variant="h6" fontWeight="bold" color="#04446e">Actifs de cette catégorie</Typography>
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" display="block" color="textSecondary">{countPending} actif(s) en attente</Typography>
                                    <Typography variant="body2" display="block" color="textSecondary">{countRejected} actif(s) rejeté(s)</Typography>
                                </Box>
                            </Box>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 5 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Filtrer par :</Typography>
                                <Select size="small" value={filtreStatut} onChange={(val) => setFiltreStatut(val)} style={{ width: 180 }}
                                    options={[
                                        { value: 'TOUS', label: 'Tous les actifs' },
                                        { value: 'EN_ATTENTE', label: 'En attente' },
                                        { value: 'REJETE', label: 'Rejetés' },
                                    ]} />
                                <Button variant="contained" size="small" startIcon={<AddCircleOutline />}
                                    disabled={!props.categoryConsulted} onClick={openAddAsset}
                                    sx={{ bgcolor: "#2A6596", textTransform: 'none' }}>
                                    Ajouter un actif
                                </Button>
                                <Input size="small" allowClear prefix={<SearchOutlined />} placeholder="Rechercher..." value={search} onChange={event => setSearch(event.target.value)} style={{ width: 210 }} />
                            </Stack>
                        </Box>

                        {!props.categoryConsulted && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Consultez d'abord une catégorie pour pouvoir y créer des actifs.
                            </Alert>
                        )}

                        <Box sx={{ maxHeight: 'calc(100vh - 380px)',
                            minHeight: '250px',
                            overflowX: 'auto',
                            width: '100%',
                            '& .ant-table-content, & .ant-table-body': {
                                '&::-webkit-scrollbar': { height: '5px', width: '5px' },
                                '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' },
                                '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '10px' },
                                '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#2A6596' },
                            } }}>
                            <AntTable
                                dataSource={dataAffichees}
                                columns={antdColumns}
                                rowKey={(record: any) => record._key || record.CODEACTIF}
                                size="small"
                                pagination={false}
                                sticky={true}
                                scroll={{ x: 'max-content' }}
                            />
                        </Box>
                    </Paper>
                </Box>
            </Box>



            {/* Modale d'ajout d'actifs */}
            <Dialog open={isModalOpen} onClose={closeAssetModal} maxWidth="md" fullWidth>
                <Box sx={{ bgcolor: '#2A6596', p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography color="white" fontWeight="bold">Dossier de l'Actif Physique</Typography>
                    <IconButton onClick={closeAssetModal} size="small" sx={{ color: 'white' }}><Close /></IconButton>
                </Box>

                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Stack spacing={2}>
                                <TextField disabled label="Code Actif (Auto)" fullWidth size="small" value={assetForm.CODEACTIF || "Généré à l'enregistrement..."} />
                                <TextField label="Numéro de série / Unique" fullWidth size="small" value={assetForm.IDENTIFIANTUNIQUE} onChange={e => setAssetForm({ ...assetForm, IDENTIFIANTUNIQUE: e.target.value })} />
                                <TextField required label="Désignation" fullWidth size="small" value={assetForm.DESIGNATION} onChange={e => setAssetForm({ ...assetForm, DESIGNATION: e.target.value })} />
                                <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666' }}>Grade de l'actif *</Typography>
                                    <Select style={{ width: '100%' }} value={assetForm.GRADE} onChange={(val) => setAssetForm({ ...assetForm, GRADE: val })}
                                        options={[{ value: 'A', label: 'Grade A (Neuf)' }, { value: 'B', label: 'Grade B (Excellent)' }, { value: 'C', label: 'Grade C (Bon)' }, { value: 'D', label: 'Grade D (Moyen)' }, { value: 'E', label: 'Grade E (Mauvais)' }]} />
                                </Box>
                                <TextField required label="Coût d'achat (XAF)" type="number" fullWidth size="small"
                                    onKeyDown={(e) => ["e", "E", "-", "+"].includes(e.key) && e.preventDefault()}
                                    value={assetForm.COUTACHAT || ''} onChange={e => setAssetForm({ ...assetForm, COUTACHAT: Number(e.target.value) })} />
                                <TextField label="Valeur Résiduelle" type="number" fullWidth size="small"
                                    onKeyDown={(e) => ["e", "E", "-", "+"].includes(e.key) && e.preventDefault()}
                                    value={assetForm.VALEURRESIDUELLE || ''} onChange={e => setAssetForm({ ...assetForm, VALEURRESIDUELLE: Number(e.target.value) })} />
                                <TextField label="Localisation courante" fullWidth size="small" value={assetForm.LOCALISATION} onChange={e => setAssetForm({ ...assetForm, LOCALISATION: e.target.value })} />
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Stack spacing={2}>
                                <SearchModal form={props.form} width={6} Titre={'Recherche Fournisseur'} label={'Fournisseur'} name={'FOURNISSEUR'} Parametre={searchFournisseur} />
                                <Divider><Typography variant="caption" sx={{ fontWeight: 'bold' }}>DOCUMENTS JUSTIFICATIFS DE L'ACTIF</Typography></Divider>
                                <Box sx={{ border: '1px solid #d9d9d9', borderRadius: 1, p: 1.5, bgcolor: '#fafafa' }}>
                                    <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 'bold' }}>
                                        Uploader un document
                                    </Typography>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Select
                                            value={selectedDocumentType}
                                            placeholder="Sélectionner un type"
                                            style={{ flex: 1 }}
                                            options={(() => {
                                                // Compter les occurrences de chaque type dans les documents uploadés
                                                const typeCounts: { [key: string]: number } = {};
                                                (assetForm.DOCUMENTS || []).forEach((doc: any) => {
                                                    typeCounts[doc.TypeCodeDoc] = (typeCounts[doc.TypeCodeDoc] || 0) + 1;
                                                });
                                                
                                                return documentDefinitions.map(document => {
                                                    const count = typeCounts[document.TypeCodeDoc] || 0;
                                                    const baseLabel = document.LibelleDefault || document.TypeCodeDoc;
                                                    return {
                                                        value: document.TypeCodeDoc,
                                                        label: count > 0 ? `${baseLabel} (${count})` : baseLabel
                                                    };
                                                });
                                            })()}
                                            onChange={setSelectedDocumentType}
                                        />
                                        <Upload
                                            beforeUpload={(file) => selectedDocumentType ? handleUpload(file, selectedDocumentType) : false}
                                            maxCount={1}
                                            showUploadList={false}
                                            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                                            disabled={!selectedDocumentType || uploadingField !== null}
                                        >
                                            <Button variant="contained" size="small" disabled={!selectedDocumentType || uploadingField !== null}>
                                                Upload
                                            </Button>
                                        </Upload>
                                    </Stack>

                                    {(assetForm.DOCUMENTS || []).length > 0 && (
                                        <Stack spacing={0.75} sx={{ mt: 1.25 }}>
                                            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 'bold', color: '#2A6596' }}>
                                                Documents uploadés :
                                            </Typography>
                                            {(() => {
                                                // Grouper les documents par type et les numéroter
                                                const groupedDocs: { [key: string]: any[] } = {};
                                                (assetForm.DOCUMENTS || []).forEach((doc: any) => {
                                                    if (!groupedDocs[doc.TypeCodeDoc]) {
                                                        groupedDocs[doc.TypeCodeDoc] = [];
                                                    }
                                                    groupedDocs[doc.TypeCodeDoc].push(doc);
                                                });
                                                
                                                return Object.entries(groupedDocs).map(([typeCode, docs]) => {
                                                    const documentType = documentDefinitions.find(item => item.TypeCodeDoc === typeCode);
                                                    const baseLabel = documentType?.LibelleDefault || typeCode;
                                                    
                                                    return docs.map((document, index) => (
                                                        <Box key={document._id} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5, bgcolor: 'white', border: '1px solid #e8e8e8', borderRadius: 1 }}>
                                                            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {index === 0 ? baseLabel : `${baseLabel} (${index})`}
                                                            </Typography>
                                                            <IconButton size="small" title="Prévisualiser" onClick={() => setPreviewedDocument(document)}>
                                                                <Visibility fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" title="Supprimer" onClick={() => removeDocument(document._id)}>
                                                                <DeleteOutline fontSize="small" color="error" />
                                                            </IconButton>
                                                        </Box>
                                                    ));
                                                }).flat();
                                            })()}
                                        </Stack>
                                    )}
                                </Box>
                            </Stack>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                    <Button variant="contained" sx={{ bgcolor: "#ccc", color: '#000' }} onClick={closeAssetModal}>Annuler</Button>
                    <Button variant="contained" sx={{ bgcolor: "#2A6596" }} startIcon={<Save />} onClick={handleAddOrUpdateAsset} disabled={uploadingField !== null}>{editingIndex !== null ? "Enregistrer les modifications" : "Enregistrer l'actif"}</Button>
                </DialogActions>
            </Dialog>
            <Modal
                open={!!previewedDocument}
                title={previewedDocument ? `Aperçu : ${previewedDocument.LibelleDefault || previewedDocument.LibelleDoc || documentDefinitions.find(item => item.TypeCodeDoc === previewedDocument.TypeCodeDoc)?.LibelleDefault || previewedDocument.TypeCodeDoc}` : 'Aperçu du document'}
                onCancel={() => setPreviewedDocument(null)}
                footer={<Button onClick={() => setPreviewedDocument(null)}>Fermer</Button>}
                width="95vw"
                style={{ maxWidth: '1500px' }}
                zIndex={2000}
                centered
                bodyStyle={{ padding: 16, background: '#f5f5f5' }}
            >
                <Box sx={{ minHeight: '70vh', maxHeight: '80vh', overflow: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {previewedDocument && String(previewedDocument.Valeur).startsWith('data:image/') ? (
                        <img src={previewedDocument.Valeur} alt="Aperçu du document" style={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain', borderRadius: 6 }} />
                    ) : previewedDocument && String(previewedDocument.Valeur).startsWith('data:application/pdf') ? (
                        <iframe src={previewedDocument.Valeur} title="Aperçu du document" style={{ width: '100%', height: '78vh', border: 0, background: '#fff' }} />
                    ) : (
                        <Typography>Aperçu intégré indisponible pour ce type de fichier.</Typography>
                    )}
                </Box>
            </Modal>
        </div>
    );
};

export default Tab1;